import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Student, StudentFormValues, StudentRow } from '../types/student';
import { fromRow, toRow } from '../utils/students';
import { STUDENTS_TABLE, describeSupabaseError, isMissingTableError, supabase } from '../utils/supabase';
import { useAuth } from './AuthContext';
export type SyncState = 'connecting' | 'live' | 'offline';
interface StudentsContextValue {
  students: Student[];
  loading: boolean;
  error: string | null;
  setupRequired: boolean;
  syncState: SyncState;
  refresh: () => Promise<void>;
  formOpen: boolean;
  editing: Student | null;
  detail: Student | null;
  openCreateForm: () => void;
  openEditForm: (student: Student) => void;
  closeForm: () => void;
  openDetail: (student: Student) => void;
  closeDetail: () => void;
  createStudent: (values: StudentFormValues) => Promise<void>;
  updateStudent: (student: Student, values: StudentFormValues) => Promise<void>;
  removeStudent: (student: Student) => Promise<void>;
}
const StudentsContext = createContext<StudentsContextValue | undefined>(undefined);
export function StudentsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user
  } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupRequired, setSetupRequired] = useState(false);
  const [syncState, setSyncState] = useState<SyncState>('connecting');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Student | null>(null);
  const [detail, setDetail] = useState<Student | null>(null);

  /** Reads this account's rows straight from the Supabase students table. */
  const load = useCallback(async (showSpinner: boolean) => {
    if (!user) {
      setStudents([]);
      setLoading(false);
      setSyncState('connecting');
      return;
    }
    if (showSpinner) setLoading(true);
    const {
      data,
      error: queryError
    } = await supabase.from(STUDENTS_TABLE).select('*').eq('owner_id', user.id).order('created_at', {
      ascending: false
    });
    if (queryError) {
      setError(describeSupabaseError(queryError));
      setSetupRequired(isMissingTableError(queryError));
      setSyncState('offline');
    } else {
      setStudents(((data ?? []) as StudentRow[]).map(fromRow));
      setError(null);
      setSetupRequired(false);
      setSyncState('live');
    }
    setLoading(false);
  }, [user]);
  useEffect(() => {
    setStudents([]);
    setError(null);
    setSetupRequired(false);
    setSyncState('connecting');
    void load(true);
  }, [load]);

  // Realtime: any insert / update / delete on this account's rows refetches.
  useEffect(() => {
    if (!user) return;
    const channel = supabase.channel(`students-${user.id}`).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: STUDENTS_TABLE,
      filter: `owner_id=eq.${user.id}`
    }, () => {
      void load(false);
    }).subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, load]);
  const createStudent = useCallback(async (values: StudentFormValues) => {
    if (!user) throw new Error('You must be signed in to add a student.');
    const {
      error: insertError
    } = await supabase.from(STUDENTS_TABLE).insert({
      ...toRow(values),
      owner_id: user.id
    });
    if (insertError) throw new Error(describeSupabaseError(insertError));
    await load(false);
  }, [load, user]);
  const updateStudent = useCallback(async (student: Student, values: StudentFormValues) => {
    const {
      error: updateError
    } = await supabase.from(STUDENTS_TABLE).update({
      ...toRow(values),
      updated_at: new Date().toISOString()
    }).eq('id', student.id);
    if (updateError) throw new Error(describeSupabaseError(updateError));
    await load(false);
  }, [load]);
  const removeStudent = useCallback(async (student: Student) => {
    const {
      error: deleteError
    } = await supabase.from(STUDENTS_TABLE).delete().eq('id', student.id);
    if (deleteError) throw new Error(describeSupabaseError(deleteError));
    await load(false);
  }, [load]);
  const refresh = useCallback(async () => {
    await load(true);
  }, [load]);
  const openCreateForm = useCallback(() => {
    setEditing(null);
    setFormOpen(true);
  }, []);
  const openEditForm = useCallback((student: Student) => {
    setEditing(student);
    setFormOpen(true);
  }, []);
  const closeForm = useCallback(() => {
    setFormOpen(false);
    setEditing(null);
  }, []);
  const openDetail = useCallback((student: Student) => setDetail(student), []);
  const closeDetail = useCallback(() => setDetail(null), []);
  const value = useMemo<StudentsContextValue>(() => ({
    students,
    loading,
    error,
    setupRequired,
    syncState,
    refresh,
    formOpen,
    editing,
    detail,
    openCreateForm,
    openEditForm,
    closeForm,
    openDetail,
    closeDetail,
    createStudent,
    updateStudent,
    removeStudent
  }), [students, loading, error, setupRequired, syncState, refresh, formOpen, editing, detail, openCreateForm, openEditForm, closeForm, openDetail, closeDetail, createStudent, updateStudent, removeStudent]);
  return <StudentsContext.Provider value={value} data-id="element-486" data-mp="203d9ie82trh8">{children}</StudentsContext.Provider>;
}
export function useStudents(): StudentsContextValue {
  const context = useContext(StudentsContext);
  if (!context) throw new Error('useStudents must be used inside StudentsProvider');
  return context;
}